import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, MapPin, Clock, Tag } from 'lucide-react';
import { eventosAPI } from '@/lib/api';
import { Calendar } from '@/components/ui/calendar';

const eventosHistoricos = [
  { id: '1', titulo: 'Torneio Beneficente ACEC', data: '2025-04-21', local: 'Canaã dos Carajás', tipo: 'torneio' },
  { id: '2', titulo: 'Treino Semanal', data: '2025-12-18', local: 'Quadra Municipal', tipo: 'treino' },
  { id: '3', titulo: 'Amistoso Inter-Equipes', data: '2025-12-20', local: 'Canaã dos Carajás', tipo: 'amistoso' },
];

export default function Eventos() {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'

  useEffect(() => {
    const fetchEventos = async () => {
      try {
        const res = await eventosAPI.listar();
        setEventos(res.data);
      } catch (error) {
        console.error('Erro ao carregar eventos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEventos();
  }, []);

  const displayEventos = eventos.length > 0 ? eventos : eventosHistoricos;

  const getTipoBadge = (tipo) => {
    const badges = {
      treino: { color: 'bg-green-100 text-green-700', label: 'Treino' },
      amistoso: { color: 'bg-blue-100 text-blue-700', label: 'Amistoso' },
      torneio: { color: 'bg-yellow-100 text-yellow-700', label: 'Torneio' },
      campeonato: { color: 'bg-purple-100 text-purple-700', label: 'Campeonato' },
    };
    return badges[tipo] || badges.treino;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const eventDates = displayEventos.map(e => new Date(e.data));

  return (
    <div data-testid="eventos-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-blue-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 hero-pattern" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <CalendarIcon className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase">
              <span className="text-yellow-400">Eventos</span> e Calendário
            </h1>
            <p className="text-xl text-blue-100 mt-4 max-w-2xl mx-auto">
              Acompanhe nossos treinos, jogos e competições
            </p>
          </motion.div>
        </div>
      </section>

      {/* Toggle View */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setViewMode('list')}
              data-testid="view-list"
              className={`px-6 py-2.5 rounded-full font-bold text-sm uppercase tracking-wider transition-all ${
                viewMode === 'list'
                  ? 'bg-blue-700 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Lista
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              data-testid="view-calendar"
              className={`px-6 py-2.5 rounded-full font-bold text-sm uppercase tracking-wider transition-all ${
                viewMode === 'calendar'
                  ? 'bg-blue-700 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Calendário
            </button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-slate-50" data-testid="eventos-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : viewMode === 'calendar' ? (
            /* Calendar View */
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 bg-white rounded-xl p-6 shadow-md">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-md w-full"
                  modifiers={{ hasEvent: eventDates }}
                  modifiersStyles={{
                    hasEvent: { backgroundColor: '#1E4ED8', color: 'white', fontWeight: 'bold' }
                  }}
                />
              </div>
              
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-6">
                  Próximos Eventos
                </h3>
                <div className="space-y-4">
                  {displayEventos.slice(0, 5).map((evento) => {
                    const badge = getTipoBadge(evento.tipo);
                    return (
                      <div
                        key={evento.id}
                        className="bg-white rounded-lg p-4 shadow-sm event-card"
                        data-testid={`calendar-evento-${evento.id}`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${badge.color}`}>
                            {badge.label}
                          </span>
                        </div>
                        <h4 className="font-heading text-lg font-bold text-slate-900">
                          {evento.titulo}
                        </h4>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <CalendarIcon size={14} />
                            {formatDate(evento.data)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {evento.local}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            /* List View */
            <div className="max-w-3xl mx-auto space-y-6">
              {displayEventos.length === 0 ? (
                <div className="text-center py-20">
                  <CalendarIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-heading text-2xl text-slate-600 mb-2">
                    Nenhum evento cadastrado
                  </h3>
                  <p className="text-slate-500">
                    Os eventos serão adicionados em breve.
                  </p>
                </div>
              ) : (
                displayEventos.map((evento, index) => {
                  const badge = getTipoBadge(evento.tipo);
                  return (
                    <motion.div
                      key={evento.id}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-xl p-6 shadow-md event-card"
                      data-testid={`evento-card-${evento.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        {/* Date Badge */}
                        <div className="flex-shrink-0 w-20 h-20 bg-blue-700 rounded-xl flex flex-col items-center justify-center text-white">
                          <span className="font-accent text-3xl font-bold">
                            {new Date(evento.data).getDate()}
                          </span>
                          <span className="text-xs uppercase">
                            {new Date(evento.data).toLocaleDateString('pt-BR', { month: 'short' })}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${badge.color}`}>
                              {badge.label}
                            </span>
                          </div>
                          <h3 className="font-heading text-xl font-bold text-slate-900 uppercase">
                            {evento.titulo}
                          </h3>
                          {evento.descricao && (
                            <p className="text-slate-600 mt-2">
                              {evento.descricao}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1.5">
                              <MapPin size={16} />
                              {evento.local}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
